// Macro Monitor View - Main dashboard for 200+ macroeconomic indicators
// Bloomberg-quality financial terminal interface
import React, { useState, useCallback, useMemo } from 'react';
import { RefreshCw, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus, Globe, Settings, Download } from 'lucide-react';
import { CategoryNavigator } from '../components/macro/CategoryNavigator';
import { QuickStatsStrip } from '../components/macro/QuickStatsStrip';
import { IndicatorDetailPanel } from '../components/macro/IndicatorDetailPanel';
import { useAPIHealth, useIndicatorData } from '../hooks/useIndicatorData';
import { getIndicatorCount } from '../data/indicatorRegistry';
import type { IndicatorCategory } from '../data/indicatorRegistry';

// Central Asia & Silk Road Corridor country options
const COUNTRY_OPTIONS = [
  { code: 'UZB', name: 'Uzbekistan', flag: '\ud83c\uddfa\ud83c\uddff' },
  { code: 'KAZ', name: 'Kazakhstan', flag: '\ud83c\uddf0\ud83c\uddff' },
  { code: 'KGZ', name: 'Kyrgyzstan', flag: '\ud83c\uddf0\ud83c\uddec' },
  { code: 'TJK', name: 'Tajikistan', flag: '\ud83c\uddf9\ud83c\uddef' },
  { code: 'TKM', name: 'Turkmenistan', flag: '\ud83c\uddf9\ud83c\uddf2' },
  { code: 'AZE', name: 'Azerbaijan', flag: '\ud83c\udde6\ud83c\uddff' },
  { code: 'GEO', name: 'Georgia', flag: '\ud83c\uddec\ud83c\uddea' },
  { code: 'MNG', name: 'Mongolia', flag: '\ud83c\uddf2\ud83c\uddf3' },
];

// Category tabs for quick navigation
const CATEGORY_TABS = [
  { id: 'gdp_growth' as IndicatorCategory, name: 'GDP & Growth', icon: TrendingUp },
  { id: 'prices' as IndicatorCategory, name: 'Prices', icon: TrendingUp },
  { id: 'external' as IndicatorCategory, name: 'External', icon: Globe },
  { id: 'fiscal' as IndicatorCategory, name: 'Fiscal', icon: TrendingUp },
  { id: 'monetary' as IndicatorCategory, name: 'Monetary', icon: TrendingUp },
];

// Hero metric configuration
const HERO_METRICS = [
  { id: 'gdp', label: 'GDP', unit: 'USD', format: 'currency', color: 'data-blue', goodDirection: 'up' },
  { id: 'gdp_growth', label: 'Real GDP Growth', unit: '%', format: 'percent', color: 'data-green', goodDirection: 'up' },
  { id: 'inflation_cpi', label: 'Inflation (CPI)', unit: '%', format: 'percent', color: 'data-amber', goodDirection: 'down' },
  { id: 'fdi_inflows', label: 'FDI Inflows', unit: 'USD', format: 'currency', color: 'data-green', goodDirection: 'up' },
];

// Format value for display
const formatHeroValue = (value: number | null, format: string): string => {
  if (value === null || value === undefined) return 'N/A';

  if (format === 'percent') {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  }
  if (format === 'currency') {
    if (Math.abs(value) >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
    if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    return `$${value.toFixed(0)}`;
  }
  return value.toFixed(1);
};

// Hero Metric Card Component
interface HeroMetricProps {
  id: string;
  label: string;
  format: string;
  color: string;
  countryCode: string;
  goodDirection: string;
  isSelected: boolean;
  onClick: () => void;
}

const HeroMetricCard: React.FC<HeroMetricProps> = ({
  id,
  label,
  format,
  color,
  countryCode,
  goodDirection,
  isSelected,
  onClick,
}) => {
  const { statistics, yoyChange, isLoading } = useIndicatorData(id, countryCode, 10);

  const latestValue = statistics?.latest ?? null;
  const latestChange = yoyChange.length > 0 ? yoyChange[yoyChange.length - 1]?.change : null;
  const latestYear = statistics?.latestYear ?? null;

  const isPositive = latestChange !== null && latestChange > 0;
  const isNegative = latestChange !== null && latestChange < 0;
  const isGood = goodDirection === 'up' ? isPositive : goodDirection === 'down' ? isNegative : true;

  if (isLoading) {
    return (
      <div className="bg-terminal-panel border border-terminal-border rounded-lg p-4 animate-pulse">
        <div className="h-3 bg-gray-700 rounded w-20 mb-2" />
        <div className="h-8 bg-gray-700 rounded w-24" />
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`bg-terminal-panel border rounded-lg p-4 text-left transition-all hover:border-${color}/50 ${
        isSelected ? `border-${color} ring-1 ring-${color}/30` : 'border-terminal-border'
      }`}
    >
      {/* Label */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</span>
        {latestYear && <span className="text-[9px] text-gray-600">{latestYear}</span>}
      </div>

      {/* Value */}
      <div className="flex items-baseline gap-2">
        <span className={`text-2xl font-mono font-bold text-${color}`}>
          {formatHeroValue(latestValue, format)}
        </span>
      </div>

      {/* Change */}
      {latestChange !== null && (
        <div className={`flex items-center gap-1 mt-1 ${isGood ? 'text-data-green' : 'text-data-red'}`}>
          {isPositive ? <TrendingUp size={12} /> : isNegative ? <TrendingDown size={12} /> : <Minus size={12} />}
          <span className="text-[11px] font-medium">
            {latestChange > 0 ? '+' : ''}{latestChange.toFixed(1)}% YoY
          </span>
        </div>
      )}
    </button>
  );
};

const MacroMonitorView: React.FC = () => {
  // State
  const [selectedIndicator, setSelectedIndicator] = useState<string>('gdp_growth');
  const [selectedCountry, setSelectedCountry] = useState<string>('UZB');
  const [selectedCategory, setSelectedCategory] = useState<IndicatorCategory>('gdp_growth');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // API Health
  const { imfHealthy, wbHealthy, checkHealth } = useAPIHealth();

  // Handle indicator selection
  const handleIndicatorSelect = useCallback((indicatorId: string) => {
    setSelectedIndicator(indicatorId);
  }, []);

  // Handle category selection
  const handleCategorySelect = useCallback((category: IndicatorCategory) => {
    setSelectedCategory(category);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await checkHealth();
    setTimeout(() => setIsRefreshing(false), 1000);
  }, [checkHealth]);

  // Handle export
  const handleExport = useCallback((indicatorId: string, format: 'csv' | 'png') => {
    console.log(`Export ${indicatorId} as ${format}`);
  }, []);

  const indicatorCount = getIndicatorCount();
  const selectedCountryData = COUNTRY_OPTIONS.find(c => c.code === selectedCountry);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-terminal-bg">
      {/* Top Header Bar */}
      <div className="h-11 bg-terminal-header border-b border-terminal-border flex items-center px-4 gap-4 shrink-0">
        {/* Title */}
        <div className="flex items-center gap-2">
          <span className="text-data-amber font-bold text-sm tracking-wide">MACRO MONITOR</span>
          <span className="text-gray-600 text-[10px]">|</span>
          <span className="text-gray-500 text-[10px]">{indicatorCount} indicators</span>
        </div>

        {/* Country Selector */}
        <div className="flex items-center gap-2 ml-4">
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="bg-terminal-bg border border-terminal-border rounded px-3 py-1.5 text-sm text-gray-300 focus:outline-none focus:border-data-amber/50 font-medium"
          >
            {COUNTRY_OPTIONS.map(country => (
              <option key={country.code} value={country.code}>
                {country.flag} {country.name}
              </option>
            ))}
          </select>
        </div>

        {/* Category Tabs */}
        <div className="flex items-center gap-1 ml-4">
          {CATEGORY_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleCategorySelect(tab.id)}
              className={`px-3 py-1 text-[10px] font-medium rounded transition-colors ${
                selectedCategory === tab.id
                  ? 'bg-data-amber/20 text-data-amber border border-data-amber/30'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-terminal-bg/50'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* API Status */}
        <div className="flex items-center gap-3 text-[9px]">
          <div className="flex items-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-full ${imfHealthy ? 'bg-data-green' : 'bg-data-red'}`} />
            <span className="text-gray-500">IMF</span>
          </div>
          <div className="flex items-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-full ${wbHealthy ? 'bg-data-green' : 'bg-data-red'}`} />
            <span className="text-gray-500">World Bank</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-1.5 text-gray-500 hover:text-white transition-colors rounded hover:bg-terminal-bg/50"
            title="Refresh data"
          >
            <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
          </button>
          <button
            className="p-1.5 text-gray-500 hover:text-white transition-colors rounded hover:bg-terminal-bg/50"
            title="Export all"
          >
            <Download size={14} />
          </button>
          <button
            className="p-1.5 text-gray-500 hover:text-white transition-colors rounded hover:bg-terminal-bg/50"
            title="Settings"
          >
            <Settings size={14} />
          </button>
        </div>
      </div>

      {/* Hero Metrics Row */}
      <div className="border-b border-terminal-border bg-terminal-panel/50 p-4 shrink-0">
        <div className="grid grid-cols-4 gap-4">
          {HERO_METRICS.map(metric => (
            <HeroMetricCard
              key={metric.id}
              id={metric.id}
              label={metric.label}
              format={metric.format}
              color={metric.color}
              countryCode={selectedCountry}
              goodDirection={metric.goodDirection}
              isSelected={selectedIndicator === metric.id}
              onClick={() => handleIndicatorSelect(metric.id)}
            />
          ))}
        </div>
      </div>

      {/* Quick Stats Strip - Additional indicators */}
      <QuickStatsStrip
        selectedIndicator={selectedIndicator}
        onIndicatorSelect={handleIndicatorSelect}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Category Navigator (Sidebar) */}
        <div
          className={`transition-all duration-300 shrink-0 ${
            isSidebarCollapsed ? 'w-0 overflow-hidden' : 'w-64'
          }`}
        >
          <CategoryNavigator
            selectedIndicator={selectedIndicator}
            onIndicatorSelect={handleIndicatorSelect}
            onCategorySelect={handleCategorySelect}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </div>

        {/* Sidebar Toggle */}
        <button
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className="w-4 flex items-center justify-center bg-terminal-header border-r border-terminal-border text-gray-500 hover:text-white transition-colors shrink-0"
        >
          {isSidebarCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>

        {/* Indicator Detail Panel */}
        <div className="flex-1 min-w-0">
          <IndicatorDetailPanel
            indicatorId={selectedIndicator}
            countryCode={selectedCountry}
            onExport={handleExport}
          />
        </div>
      </div>

      {/* Bottom Status Bar */}
      <div className="h-6 bg-terminal-header border-t border-terminal-border flex items-center px-4 gap-4 text-[9px] text-gray-500 shrink-0">
        <span className="flex items-center gap-1">
          <Globe size={10} />
          {selectedCountryData?.flag} {selectedCountryData?.name}
        </span>
        <span className="text-gray-600">|</span>
        <span>Sources: IMF WEO, World Bank, Central Bank of Uzbekistan</span>
        <div className="flex-1" />
        <span>Updated: {new Date().toLocaleDateString()}</span>
      </div>
    </div>
  );
};

export default MacroMonitorView;
